import type {
  PriceListItem,
  PriceListPickerItem,
  QuoteCatalogItem,
} from "@xop-any-time/shared";

import { getPriceList, getQuoteCatalog } from "../../lib/crmApi.js";

/**
 * The portal's price-list policy — everything the controller's ruling
 * (spec §6) adds on top of the command center's picker.
 *
 * IT LIVES HERE, OUTSIDE `src/crm`, on purpose. The ported tree is a copy
 * that must stay diffable against xop-edge-staff and is excluded from this
 * repo's type program, so a rule written in there would be neither
 * typechecked nor comfortably testable. What the one edited ported file
 * (the command center's `generate-invoice-form/products-table.tsx`) does is CALL these three pure
 * functions; the reasoning, the strings and the tests are all here.
 */

/**
 * The projection the BFF sends, widened into the shape the ported MUI picker
 * reads.
 *
 * `active: true` because crm-api's reseller index serves active items only
 * (spec §4.2) and the ported table filters on the flag; `notes: ""` because
 * the notes are XOP Networks' internal annotation on the book and the ported
 * search haystack reads the key. Both constants are stated once, here, rather
 * than in the ported file where they would look like data.
 */
export function toPickerItems(items: readonly PriceListItem[]): PriceListPickerItem[] {
  return items.map((item) => ({ ...item, notes: "", active: true }));
}

/**
 * The list, for the builder. NEVER REJECTS.
 *
 * A failed fetch and an empty book are deliberately the same answer: both
 * mean "there is nothing to quote from", and both must leave the reseller
 * with the manual and catalog modes rather than a dead form. The BFF has
 * already logged the real reason; a toast here would ask a reseller to act on
 * something only XOP Networks can fix.
 */
export async function fetchPriceListForPicker(): Promise<PriceListPickerItem[]> {
  try {
    const body = await getPriceList();
    return toPickerItems(body.items ?? []);
  } catch {
    return [];
  }
}

/**
 * The catalogue the REQUEST FORM reads — identity and unit, no money.
 *
 * A separate call from `fetchPriceListForPicker` on purpose: that one feeds a
 * builder that prices lines, this one feeds a form that must not be able to.
 * `/quote-catalog` drops every price field at the server, so "no prices on the
 * request form" is a property of the payload rather than of the component.
 *
 * NEVER REJECTS, for the same reason as the picker: a failed fetch and an
 * empty catalogue both mean "there is nothing to ask for", and the page says
 * so instead of showing a reseller an error only XOP can act on.
 */
export async function fetchQuoteCatalog(): Promise<QuoteCatalogItem[]> {
  try {
    const body = await getQuoteCatalog();
    return body.items ?? [];
  } catch {
    return [];
  }
}

/**
 * Is the builder locked to the price list?
 *
 * THE RULING (spec §6): while the list has at least one active item, every
 * line of a reseller quote or invoice is a CEO-priced item, so the manual and
 * catalog modes are not offered. When it is empty — before the migration has
 * run, or if the fetch failed — quoting falls back to the previous modes and
 * nothing breaks. A reseller who needs a custom line asks XOP Networks to add
 * it to the list.
 */
export function priceListLocked(items: readonly { active: boolean }[]): boolean {
  return items.some((i) => i.active);
}

/**
 * The one-line warning when an item is priced in a currency the reseller does
 * not bill in.
 *
 * NO CONVERSION, deliberately (spec §8): a rate the portal invented would be
 * wrong by the time the customer paid, and the number on the quote is the
 * number XOP Networks published. Silent when either side is unknown — a
 * warning naming an empty currency reads as a bug, not a caution.
 */
export function priceListCurrencyWarning(
  itemCurrency: string,
  accountCurrency: string,
): string | null {
  const item = itemCurrency.trim().toUpperCase();
  const account = accountCurrency.trim().toUpperCase();
  if (!item || !account || item === account) return null;
  return `This item is priced in ${item}. Your account bills in ${account}. XOP Networks does not convert the price — it is copied as-is.`;
}
